import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { PayoutBatchDetail } from "../../types/payoutBatches.types";

export interface BatchTotalsPanelProps {
  batch: PayoutBatchDetail;
}

/** What the selection adds up to: how many payments, what beneficiaries receive, fees, and the debit. */
export function BatchTotalsPanel({ batch }: BatchTotalsPanelProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const delivery = batch.currency;
  const source = batch.source_currency;
  const fx = batch.summary.fx;
  const crossCurrency = source !== delivery;

  return (
    <ColocaSurface padding="lg" className="flex h-full flex-col gap-5">
      <div className="space-y-1">
        <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
          {t("totals_title")}
        </p>
        <p className="text-2xl font-semibold tracking-tight text-foreground tabular-nums">
          {t("totals_payments", { count: batch.counters.selected })}
        </p>
      </div>

      <dl className="space-y-2 text-sm">
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-muted-foreground">{t("totals_receive")}</dt>
          <dd>
            <ColocaAmountDisplay
              value={batch.totals.receive_selected}
              currency={delivery}
              fractionDigits={minorUnitsFor(delivery)}
              size="sm"
            />
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-muted-foreground">{t("totals_fees")}</dt>
          <dd>
            <ColocaAmountDisplay
              value={batch.totals.fees_selected}
              currency={delivery}
              fractionDigits={minorUnitsFor(delivery)}
              size="sm"
            />
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-4 border-t border-border pt-2">
          <dt className="font-medium text-foreground">{t("totals_debit")}</dt>
          <dd>
            <ColocaAmountDisplay
              value={batch.totals.debit_indicative_selected}
              currency={source}
              fractionDigits={minorUnitsFor(source)}
              size="md"
            />
          </dd>
        </div>
        {crossCurrency && fx?.rate_indicative && (
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-muted-foreground">{t("totals_rate")}</dt>
            <dd className="text-right">
              <span className="font-mono text-sm text-foreground tabular-nums">
                1 {source} = {fx.rate_indicative} {delivery}
              </span>
              {fx.priced_at && (
                <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
                  {t("totals_priced_at", {
                    time: formatDateTime(fx.priced_at, {
                      locale: activeLanguage,
                      includeTime: true,
                    }),
                  })}
                </p>
              )}
            </dd>
          </div>
        )}
      </dl>

      {crossCurrency && <p className="mt-auto text-xs text-muted-foreground">{t("totals_fx_notice")}</p>}
    </ColocaSurface>
  );
}
